import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  message,
  Modal,
  Segmented,
  Table,
  Space,
  Select,
  Card,
  Row,
  Col,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import {
  SearchOutlined,
  FilterFilled,
  KeyOutlined,
  DeleteOutlined,
  UserAddOutlined,
  DownOutlined,
  UpOutlined,
  CloseOutlined,
  FilterOutlined,
} from "@ant-design/icons";

import Search from "antd/es/transfer/search";
import {
  addStaff,
  changePasswordToAdmin,
  loadAllStaff,
  updateUser,
  uploadUsersFromExcel,
} from "../../redux/rtk/features/user/userSlice";
import AttendBtn from "../Buttons/AttendBtn";
import ViewBtn from "../Buttons/ViewBtn";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import UserDataExport from "./userExportData";

import "./user.css";
import { toast } from "react-toastify";
import FilterPanel from "../hiring/FilterPanel";
import "../hiring/FilterPanel.css";

function CustomTable({ list }) {
  const dispatch = useDispatch();
  const searchInputRefs = useRef({}); // Store refs for each column's input
  const [activeTab, setActiveTab] = useState(1); // 1 for active users, 2 for inactive users
  const [columnsToShow, setColumnsToShow] = useState([]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isFileUploadModalVisible, setIsFileUploadModalVisible] =
    useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [UserName, setUserName] = useState("");
  const [file, setFile] = useState(null);
  const { loading } = useSelector((state) => state.users);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [passwordResetResult, setPasswordResetResult] = useState(null);
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isPasswordResultModalVisible, setIsPasswordResultModalVisible] =
    useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    contact: "",
    role: [],
    designation: [],
    employmentType: [],
    department: [],
    shift: [],
    joinDateRange: null,
    exitDateRange: null,
  });

  // Filter the list based on activeTab (DataStatus)
  const filteredByStatusList = useMemo(() => {
    if (!list) return [];
    
    return list.filter((item) => {
      const itemDataStatus = item.DataStatus;
      // Active tab (1) should show DataStatus = 1
      // Inactive tab (2) should show DataStatus = 2
      return itemDataStatus === activeTab;
    });
  }, [list, activeTab]);

  // Get unique values for dropdown filters
  const getUniqueFilterValues = (field) => {
    if (!filteredByStatusList) return [];
    const values = filteredByStatusList
      .map((item) => item[field])
      .filter((value, index, self) => value && self.indexOf(value) === index);
    return values.sort((a, b) => a.localeCompare(b));
  };

  const roles = getUniqueFilterValues("RoleName");
  const designations = getUniqueFilterValues("DesignationName");
  const employmentTypes = getUniqueFilterValues("EmploymentTypeName");
  const departments = getUniqueFilterValues("DepartmentName");
  const shifts = getUniqueFilterValues("ShiftName");

  // Get counts for each tab
  const activeUserCount = list?.filter(item => item.DataStatus === 1).length || 0;
  const inactiveUserCount = list?.filter(item => item.DataStatus === 2).length || 0;

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    if (UserName.trim() === "") {
      message.warning("Please enter a UserName.");
      return;
    }
    setIsConfirmModalVisible(true);
  };

  const handleDelete = async (employeeId, record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this employee?",
      // content: 'This will set the employee status to inactive (DataStatus=2)',
      async onOk() {
        try {
          // Get the current employee data to send complete information
          const currentEmployee = list?.find(
            (emp) => emp.EmployeeId === employeeId
          );
          if (!currentEmployee) {
            message.error("Employee data not found");
            return;
          }

          const payload = {
            EmployeeId: employeeId,
            DataStatus: 2,
            // Include all required fields that the API needs
            GenderTypeId: currentEmployee.GenderTypeId || null,
            TitleTypeId: currentEmployee.TitleTypeId || null,
            FirstName: currentEmployee.FirstName || "",
            MiddleName: currentEmployee.MiddleName || null,
            LastName: currentEmployee.LastName || "",
            FatherName: currentEmployee.FatherName || null,
            BloodGroupTypeId: currentEmployee.BloodGroupTypeId || null,
            BirthDate: currentEmployee.BirthDate || null,
            BirthAnniversary: currentEmployee.BirthAnniversary || null,
            MaritalStatus: currentEmployee.MaritalStatus || false,
            MarriageAnniversary: currentEmployee.MarriageAnniversary || null,
            PanNumber: currentEmployee.PanNumber || null,
            AadharNumber: currentEmployee.AadharNumber || null,
            EmailId: currentEmployee.EmailId || "",
            PhoneNumber:
              currentEmployee.PhoneNumber ||
              currentEmployee.MobileNumber ||
              null,
            AlternateNumber: currentEmployee.AlternateNumber || null,
          };

          // console.log("Deactivating employee with payload:", payload);
          const resp = await dispatch(updateUser(payload));
          // console.log("Update response:", resp);

          if (resp.payload.message === "success") {
            message.success("Employee status updated successfully");

            // Force refresh the list to ensure UI updates
            setTimeout(() => {
              dispatch(loadAllStaff());
            }, 100);
          } else {
            message.error("Failed to update employee status");
          }
        } catch (error) {
          // console.error("Error updating employee status:", error);
          message.error("Error updating employee status");
        }
      },
      onCancel() {
        // Do nothing
      },
    });
  };

  const handleActivate = async (employeeId, record) => {
    Modal.confirm({
      title: "Are you sure you want to activate this employee?",
      async onOk() {
        try {
          // Get the current employee data to send complete information
          const currentEmployee = list?.find(
            (emp) => emp.EmployeeId === employeeId
          );
          if (!currentEmployee) {
            message.error("Employee data not found");
            return;
          }

          const payload = {
            EmployeeId: employeeId,
            DataStatus: 1,
            // Include all required fields that the API needs
            GenderTypeId: currentEmployee.GenderTypeId || null,
            TitleTypeId: currentEmployee.TitleTypeId || null,
            FirstName: currentEmployee.FirstName || "",
            MiddleName: currentEmployee.MiddleName || null,
            LastName: currentEmployee.LastName || "",
            FatherName: currentEmployee.FatherName || null,
            BloodGroupTypeId: currentEmployee.BloodGroupTypeId || null,
            BirthDate: currentEmployee.BirthDate || null,
            BirthAnniversary: currentEmployee.BirthAnniversary || null,
            MaritalStatus: currentEmployee.MaritalStatus || false,
            MarriageAnniversary: currentEmployee.MarriageAnniversary || null,
            PanNumber: currentEmployee.PanNumber || null,
            AadharNumber: currentEmployee.AadharNumber || null,
            EmailId: currentEmployee.EmailId || "",
            PhoneNumber:
              currentEmployee.PhoneNumber ||
              currentEmployee.MobileNumber ||
              null,
            AlternateNumber: currentEmployee.AlternateNumber || null,
          };

          // console.log("Activating employee with payload:", payload);
          const resp = await dispatch(updateUser(payload));
          // console.log("Update response:", resp);

          if (resp.payload.message === "success") {
            message.success("Employee activated successfully");

            // Force refresh the list to ensure UI updates
            setTimeout(() => {
              dispatch(loadAllStaff());
            }, 100);
          } else {
            message.error("Failed to activate employee");
          }
        } catch (error) {
          // console.error("Error activating employee:", error);
          message.error("Error activating employee");
        }
      },
      onCancel() {
        // Do nothing
      },
    });
  };

  const handleResetPassword = (employeeId) => {
    setCurrentEmployeeId(employeeId);
    setIsConfirmModalVisible(true);
  };

  const handleConfirm = async () => {
    const result = await dispatch(changePasswordToAdmin(currentEmployeeId));
    if (result?.payload?.data) {
      setPasswordResetResult(result.payload.data);
      setIsPasswordResultModalVisible(true);
    }
    setIsConfirmModalVisible(false);
  };

  const handlePasswordResultModalClose = () => {
    setIsPasswordResultModalVisible(false);
    setPasswordResetResult(null);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setUserName("");
  };

  const handleConfirmCancel = () => {
    setIsConfirmModalVisible(false);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && !selectedFile.name.endsWith(".csv")) {
      toast.error("Please select a valid CSV file.");
      setFile(null);
      return;
    }
    setFile(selectedFile);
  };

  const downloadFile = (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadSampleExcel = () => {
    downloadFile("/UserUploadExcel/employess.csv", "employess.csv");
  };

  const showFileUploadModal = () => {
    setIsFileUploadModalVisible(true);
    setFile(null); // Reset file on modal open
    setIsEditMode(false); // Reset edit mode
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a CSV file to upload.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("isEdit", isEditMode ? "true" : "false");

    try {
      await dispatch(uploadUsersFromExcel(formData)).unwrap();
      dispatch(loadAllStaff()); // Refresh the list
      setIsFileUploadModalVisible(false);
      setFile(null);
      setIsEditMode(false);
    } catch (error) {
      // Error is already handled in the thunk with toast
    } finally {
      setUploading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter && sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      search: "",
      contact: "",
      role: [],
      designation: [],
      employmentType: [],
      department: [],
      shift: [],
      joinDateRange: null,
      exitDateRange: null,
    });
  };

  const renderFilterDropdown = (field, options) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{ padding: 8, minWidth: 200 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder={`Select ${field}`}
          value={filters[field]}
          onChange={(value) => {
            handleFilterChange(field, value);
            setSelectedKeys(value);
          }}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: "100%", marginBottom: 8 }}
        >
          {options.map((option) => (
            <Select.Option key={option} value={option}>
              {option}
            </Select.Option>
          ))}
        </Select>
        <div
          style={{
            marginTop: 8,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            size="small"
            style={{ width: 90 }}
            onClick={() => {
              handleFilterChange(field, []);
              clearFilters();
              confirm();
            }}
          >
            Clear
          </Button>
          <Button
            type="primary"
            size="small"
            style={{ width: 90 }}
            onClick={() => confirm()}
          >
            OK
          </Button>
        </div>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        style={{ color: filters[field].length > 0 ? "#1890ff" : undefined }}
      />
    ),
  });

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
        <Input
          ref={(el) => (searchInputRefs.current[dataIndex] = el)} // Store ref by dataIndex
          placeholder={`Search ${title}`}
          value={filters[dataIndex]}
          onChange={(e) => {
            handleFilterChange(dataIndex, e.target.value);
            setSelectedKeys(e.target.value ? [e.target.value] : []);
          }}
          style={{ width: "100%", marginBottom: 8 }}
          autoFocus
        />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            size="small"
            style={{ width: 90 }}
            onClick={() => {
              handleFilterChange(dataIndex, "");
              clearFilters();
              confirm();
            }}
          >
            Clear
          </Button>
          <Button
            type="primary"
            size="small"
            style={{ width: 90 }}
            onClick={() => confirm()}
          >
            OK
          </Button>
        </div>
      </div>
    ),
    filterIcon: ({ filtered }) => (
      <SearchOutlined
        style={{
          color: filtered || filters[dataIndex] ? "#1890ff" : undefined,
        }}
        onClick={() => {
          // Focus the input when the search icon is clicked
          setTimeout(() => {
            if (searchInputRefs.current[dataIndex]) {
              searchInputRefs.current[dataIndex].focus();
            }
          }, 100); // Small delay to ensure dropdown is rendered
        }}
      />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible && searchInputRefs.current[dataIndex]) {
        setTimeout(() => {
          searchInputRefs.current[dataIndex].focus();
        }, 100); // Delay to ensure input is rendered
      }
    },
  });

  const columns = useMemo(
    () => [
      {
        id: 1,
        title: "ID",
        dataIndex: "serial",
        key: "serial",
        sorter: (a, b) => a.serial - b.serial,
      },
      {
        id: 2,
        title: "Name",
        key: "fullName",
        render: (record) =>
          `${record.TitleTypeName} ${record.FirstName} ${record.MiddleName || ""} ${record.LastName || ""}`
            .trim()
            .toUpperCase(),
        sorter: (a, b) => {
          const nameA = `${a.TitleTypeName || ""} ${a.FirstName || ""} ${
            a.MiddleName || ""
          } ${a.LastName || ""}`
            .trim()
            .toUpperCase();
          const nameB = `${b.TitleTypeName || ""} ${b.FirstName || ""} ${
            b.MiddleName || ""
          } ${b.LastName || ""}`
            .trim()
            .toUpperCase();
          return nameA.localeCompare(nameB);
        },
      },
      {
        id: 3,
        title: "Employee Code",
        dataIndex: "EmployeeCode",
        key: "EmployeeCode",
        sorter: (a, b) =>
          (a.EmployeeCode || "").localeCompare(b.EmployeeCode || ""),
      },
      {
        id: 4,
        title: "Role",
        dataIndex: "RoleName",
        key: "RoleName",
        render: (text) => text || "N/A",
        sorter: (a, b) => (a.RoleName || "").localeCompare(b.RoleName || ""),
      },
      {
        id: 5,
        title: "Designation",
        dataIndex: "DesignationName",
        key: "DesignationName",
        render: (text) => text || "N/A",
        sorter: (a, b) =>
          (a.DesignationName || "").localeCompare(b.DesignationName || ""),
      },
      {
        id: 6,
        title: "Employment Type",
        dataIndex: "EmploymentTypeName",
        key: "EmploymentTypeName",
        render: (text) => text || "N/A",
        sorter: (a, b) =>
          (a.EmploymentTypeName || "").localeCompare(
            b.EmploymentTypeName || ""
          ),
      },
      {
        id: 7,
        title: "Department",
        dataIndex: "DepartmentName",
        key: "DepartmentName",
        render: (text) => text || "N/A",
        sorter: (a, b) =>
          (a.DepartmentName || "").localeCompare(b.DepartmentName || ""),
      },
      {
        id: 8,
        title: "Shift",
        dataIndex: "ShiftName",
        key: "ShiftName",
        render: (text) => text || "N/A",
        sorter: (a, b) => (a.ShiftName || "").localeCompare(b.ShiftName || ""),
      },
      {
        id: 9,
        title: "Action",
        dataIndex: "EmployeeId",
        key: "action",
        render: (id) => (
          <div className="flex justify-start">
            <ViewBtn path={`/admin/hr/staffs/${id}/`} />
            <AttendBtn path={`/admin/attendance/user/${id}`} userId={id} />
            <Button
              icon={<KeyOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleResetPassword(id);
              }}
              title="Reset Password"
              className="mr-2"
              type="default"
              size="small"
            />
            {/* Show Activate button for inactive employees, Delete button for active employees */}
            {/* {(() => {
              // Find the current employee to check their status
              const currentEmployee = list?.find(
                (emp) => emp.EmployeeId === id
              );
              
              // Check the actual DataStatus of the employee
              const employeeDataStatus = currentEmployee?.DataStatus;

              // Debug logging to understand what's happening
              // console.log(`Employee ${id} button logic:`, {
                employeeId: id,
                currentEmployee: currentEmployee,
                employeeDataStatus: employeeDataStatus,
                activeTab: activeTab,
                listLength: list?.length || 0,
                hasList: !!list
              });

              // If we don't have the employee data yet, show button based on current tab
              if (!currentEmployee || employeeDataStatus === undefined) {
                // console.log(`No employee data for ${id}, using activeTab: ${activeTab}`);
                if (activeTab === 1) {
                  return (
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(id);
                      }}
                      title="Delete Employee"
                      className="mr-2"
                      type="default"
                      size="small"
                      danger
                    />
                  );
                } else {
                  return (
                    <Button
                      icon={<UserAddOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActivate(id);
                      }}
                      title="Activate Employee"
                      className="mr-2"
                      type="default"
                      size="small"
                      style={{ color: "#52c41a", borderColor: "#52c41a" }}
                    />
                  );
                }
              }

              // Show button based on the employee's actual status
              if (employeeDataStatus === 1) {
                // Employee is active (DataStatus = 1), show delete button
                return (
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(id);
                    }}
                    title="Delete Employee"
                    className="mr-2"
                    type="default"
                    size="small"
                    danger
                  />
                );
              } else if (employeeDataStatus === 2) {
                // Employee is inactive (DataStatus = 2), show activate button
                return (
                  <Button
                    icon={<UserAddOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleActivate(id);
                    }}
                    title="Activate Employee"
                    className="mr-2"
                    type="default"
                    size="small"
                    style={{ color: "#52c41a", borderColor: "#52c41a" }}
                  />
                );
              } else {
                // Fallback case - show button based on current tab
                // console.log(`Fallback button logic for employee ${id} - using activeTab: ${activeTab}`);
                if (activeTab === 1) {
                  return (
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(id);
                      }}
                      title="Delete Employee"
                      className="mr-2"
                      type="default"
                      size="small"
                      danger
                    />
                  );
                } else {
                  return (
                    <Button
                      icon={<UserAddOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActivate(id);
                      }}
                      title="Activate Employee"
                      className="mr-2"
                      type="default"
                      size="small"
                      style={{ color: "#52c41a", borderColor: "#52c41a" }}
                    />
                  );
                }
              }
            })()} */}
          </div>
        ),
      },
    ],
    [activeTab]
  );

  const onChange = (value) => {
    setActiveTab(value);
    // Clear filters when switching tabs to avoid confusion
    clearAllFilters();
  };

  useEffect(() => {
    setColumnsToShow(columns); // Set initial columns only once
  }, [columns]); // Add columns as dependency to ensure it updates

  const columnsToShowHandler = (selectedColumns) => {
    setColumnsToShow(selectedColumns); // Update based on user selection
  };

  // Debug effect to see when list changes
  useEffect(() => {
    // // console.log("List state changed:", {
    //   listExists: !!list,
    //   listLength: list?.length || 0,
    //   loading: loading,
    //   activeTab: activeTab,
    //   sampleItem: list?.[0],
    //   filteredCount: filteredByStatusList?.length || 0,
    //   activeUserCount,
    //   inactiveUserCount,
    // });
  }, [list, loading, activeTab, filteredByStatusList, activeUserCount, inactiveUserCount]);

  const addKeys = (arr) =>
    arr.map((i, index) => ({ ...i, key: i.EmployeeId, serial: index + 1 }));

  let searchFilteredList = filteredByStatusList;
  // console.log("searchFilteredList", searchFilteredList);
  

  if (searchFilteredList) {
    // Apply search filter (name, code, email, or phone - single search box)
    if (filters.search) {
      const searchValue = filters.search.toUpperCase();
      searchFilteredList = searchFilteredList.filter((item) => {
        const fullName = `${item.TitleTypeName || ""} ${item.FirstName || ""} ${
          item.MiddleName || ""
        } ${item.LastName || ""}`
          .trim()
          .toUpperCase();
        const employeeCode = (item.EmployeeCode || "").toUpperCase();
        const personalEmail = (item.EmailId || "").toUpperCase();
        const officialEmail = (item.OfficialMailId || "").toUpperCase();
        const phoneNumber = (item.PhoneNumber || item.MobileNumber || "").toString().toUpperCase();
        const alternateNumber = (item.AlternateNumber || "").toString().toUpperCase();
        return (
          fullName.includes(searchValue) ||
          employeeCode.includes(searchValue) ||
          personalEmail.includes(searchValue) ||
          officialEmail.includes(searchValue) ||
          phoneNumber.includes(searchValue) ||
          alternateNumber.includes(searchValue)
        );
      });
    }

    if (filters.role.length > 0) {
      searchFilteredList = searchFilteredList.filter((item) =>
        filters.role.includes(item.RoleName)
      );
    }

    if (filters.designation.length > 0) {
      searchFilteredList = searchFilteredList.filter((item) =>
        filters.designation.includes(item.DesignationName)
      );
    }

    if (filters.employmentType.length > 0) {
      searchFilteredList = searchFilteredList.filter((item) =>
        filters.employmentType.includes(item.EmploymentTypeName)
      );
    }

    if (filters.department.length > 0) {
      searchFilteredList = searchFilteredList.filter((item) =>
        filters.department.includes(item.DepartmentName)
      );
    }

    if (filters.shift.length > 0) {
      searchFilteredList = searchFilteredList.filter((item) =>
        filters.shift.includes(item.ShiftName)
      );
    }

    // Join Date range filter
    if (filters.joinDateRange && filters.joinDateRange[0] && filters.joinDateRange[1]) {
      const [joinFrom, joinTo] = filters.joinDateRange;
      const fromStart = joinFrom.startOf("day");
      const toEnd = joinTo.endOf("day");
      searchFilteredList = searchFilteredList.filter((item) => {
        const joinDate = item.JoinDate ? dayjs(item.JoinDate) : null;
        if (!joinDate) return false;
        return (joinDate.isAfter(fromStart) || joinDate.isSame(fromStart)) && (joinDate.isBefore(toEnd) || joinDate.isSame(toEnd));
      });
    }

    // Exit Date range filter: include employees with exit date in range OR no exit date (null)
    if (filters.exitDateRange && filters.exitDateRange[0] && filters.exitDateRange[1]) {
      const [exitFrom, exitTo] = filters.exitDateRange;
      const fromStart = exitFrom.startOf("day");
      const toEnd = exitTo.endOf("day");
      searchFilteredList = searchFilteredList.filter((item) => {
        if (!item.ExitDate) return true; // no exit date = still employed, include in results
        const exitDate = dayjs(item.ExitDate);
        return (exitDate.isAfter(fromStart) || exitDate.isSame(fromStart)) && (exitDate.isBefore(toEnd) || exitDate.isSame(toEnd));
      });
    }
  }

  return (
    <>
      <div className="ant-card p-4 rounded mt-5 get-all-user-container">
        <div className="flex my-2 justify-between flex-wrap gap-2">
          <div className="w-50">
            <h4 className="text-xl mb-2">Employee List</h4>
            <p className="text-gray-600">
              {activeTab === 1 ? "Active" : "Inactive"} Users: {searchFilteredList?.length ?? filteredByStatusList?.length ?? 0} of {list?.length || 0} total
            </p>
          </div>
          <div className="flex align-items-center filter-panel-wrap flex-wrap" style={{ position: "relative", gap: "10px" }}>
            {list && (
              <div style={{ marginLeft: "10px" }}>
                <UserDataExport
                  userData={filteredByStatusList}
                  filteredData={searchFilteredList}
                />
              </div>
            )}
            {list && (
              <>
                <ColVisibilityDropdown
                  options={columns}
                  columns={columns}
                  columnsToShowHandler={columnsToShowHandler}
                />
                <Button
                  type="primary"
                  onClick={showFileUploadModal}
                >
                  Upload CSV
                </Button>
              </>
            )}
            <button
              type="button"
              className="filter-panel-trigger"
              onClick={() => setFilterPanelOpen((o) => !o)}
              aria-label="Filters"
              style={{ marginLeft: "10px" }}
            >
              <FilterOutlined style={{ fontSize: 16 }} />
            </button>
            <FilterPanel
              open={filterPanelOpen}
              onClose={() => setFilterPanelOpen(false)}
              search={{
                placeholder: "Search by name, code, email, phone",
                value: filters.search,
                onChange: (value) => handleFilterChange("search", value),
              }}
              filters={[
                {
                  key: "role",
                  label: "Role",
                  placeholder: "All Roles",
                  value: filters.role && filters.role.length > 0 ? filters.role[0] : "",
                  onChange: (value) => handleFilterChange("role", value ? [value] : []),
                  options: [
                    { value: "", label: "All Roles" },
                    ...(roles || []).map((r) => ({ value: r, label: r })),
                  ],
                },
                {
                  key: "designation",
                  label: "Designation",
                  placeholder: "All Designations",
                  value: filters.designation && filters.designation.length > 0 ? filters.designation[0] : "",
                  onChange: (value) => handleFilterChange("designation", value ? [value] : []),
                  options: [
                    { value: "", label: "All Designations" },
                    ...(designations || []).map((d) => ({ value: d, label: d })),
                  ],
                },
                {
                  key: "department",
                  label: "Department",
                  placeholder: "All Departments",
                  value: filters.department && filters.department.length > 0 ? filters.department[0] : "",
                  onChange: (value) => handleFilterChange("department", value ? [value] : []),
                  options: [
                    { value: "", label: "All Departments" },
                    ...(departments || []).map((d) => ({ value: d, label: d })),
                  ],
                },
              ]}
            />
            <Modal
              title="Password Reset Result"
              visible={isPasswordResultModalVisible}
              onCancel={handlePasswordResultModalClose}
              footer={[
                <Button
                  key="close"
                  type="primary"
                  onClick={handlePasswordResultModalClose}
                >
                  Close
                </Button>,
              ]}
            >
              {passwordResetResult && (
                <div>
                  <p>
                    <strong>{passwordResetResult.message}</strong>
                  </p>
                  {passwordResetResult.email_status?.map((item, index) => (
                    <div key={index} className="mt-3 p-3 border rounded">
                      <p>
                        <strong>Employee Name:</strong> {item.EmployeeName}
                      </p>
                      <p>
                        <strong>Username:</strong> {item.UserName}
                      </p>
                      <p>
                        <strong>New Password:</strong> {item.UserPassword}
                      </p>
                      <p>
                        <strong>Email Status:</strong>{" "}
                        {item.EmailSendStatus ? "Sent" : "Failed"}
                      </p>
                      <p>
                        <strong>Email:</strong> {item.EmailId}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Modal>

            <Modal
              title="Confirm Password Reset"
              visible={isConfirmModalVisible}
              onOk={handleConfirm}
              onCancel={handleConfirmCancel}
              okText="Yes"
              cancelText="No"
            >
              <p>
                Are you sure you want to reset the password for this employee?
              </p>
            </Modal>
            <Modal
              title="Upload User Data"
              visible={isFileUploadModalVisible}
              onOk={handleUpload}
              onCancel={() => setIsFileUploadModalVisible(false)}
              okText="Upload"
              cancelText="Cancel"
            >
              <div>
                <div>
                  <label htmlFor="fileUpload">Select CSV file:</label>
                  <input
                    id="fileUpload"
                    type="file"
                    onChange={handleFileChange}
                    accept=".csv"
                  />
                </div>
                <div style={{ marginTop: "10px" }}>
                  <Button onClick={handleDownloadSampleExcel} type="dashed">
                    Download Sample File
                  </Button>
                </div>
              </div>
            </Modal>
            <Modal
              title="Confirm Reset"
              visible={isConfirmModalVisible}
              onOk={handleConfirm}
              onCancel={handleConfirmCancel}
              okText="Confirm"
              cancelText="Cancel"
            >
              <p>
                Are you sure you want to reset the password for user{" "}
                <strong>{UserName}</strong>?
              </p>
            </Modal>
            <Segmented
              className="text-center rounded text-red-500"
              size="middle"
              options={[
                {
                  label: (
                    <span>
                      <i className="bi bi-person-lines-fill"></i> Active ({activeUserCount})
                    </span>
                  ),
                  value: 1,
                },
                {
                  label: (
                    <span>
                      <i className="bi bi-person-dash-fill"></i> Inactive ({inactiveUserCount})
                    </span>
                  ),
                  value: 2,
                },
              ]}
              value={activeTab}
              onChange={onChange}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="ant-spin ant-spin-lg ant-spin-spinning">
              <span className="ant-spin-dot ant-spin-dot-spin">
                <i className="ant-spin-dot-item"></i>
                <i className="ant-spin-dot-item"></i>
                <i className="ant-spin-dot-item"></i>
                <i className="ant-spin-dot-item"></i>
              </span>
            </div>
            <p className="mt-4 text-gray-600">Loading employees...</p>
          </div>
        ) : !list || list.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">No employees found.</p>
          </div>
        ) : (
          <Table
            scroll={{ x: true, y: "100%" }}
            pagination={{
              defaultPageSize: 20,
            }}
            columns={columnsToShow}
            dataSource={searchFilteredList ? addKeys(searchFilteredList) : []}
            onChange={handleTableChange}
            className="get-all-user-table"
          />
        )}
      </div>
    </>
  );
}

const GetAllCust = (props) => {
  const dispatch = useDispatch();
  const list = useSelector((state) => state.users.list);

  useEffect(() => {
    dispatch(loadAllStaff());
  }, [dispatch]);

  return (
    <div className="">
      <div className="card-body">
        
        <CustomTable list={list} />
      </div>
    </div>
  );
};

export default GetAllCust;
